const clean = (value) => String(value ?? "").trim();
export const parkingKey = (value) =>
  clean(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .toUpperCase();
export const parkingNumber = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  Number.isFinite(Number(value)) &&
  Number(value) >= 0
    ? Number(value)
    : null;
export function parkingDate(value) {
  if (!value || !Number.isFinite(Date.parse(value))) return "Sin datos";
  return new Date(value).toLocaleString("es-CO", {
    timeZone: "America/Bogota",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}
export function parkingDuration(value) {
  const minutes = parkingNumber(value);
  if (minutes === null) return "Sin datos";
  const rounded = Math.round(minutes),
    days = Math.floor(rounded / 1440),
    hours = Math.floor((rounded % 1440) / 60),
    rest = rounded % 60;
  return days ? `${days} d ${hours} h` : hours ? `${hours} h ${rest} min` : `${rest} min`;
}
export function inactiveParking(row, now = Date.now()) {
  if (row.sin_ciclos === "Mayor a 3 días") return true;
  const last = Date.parse(row.sin_ciclos);
  return Number.isFinite(last) && now - last >= 3 * 86400000;
}
export function parkingActivity(row) {
  if (row.sin_ciclos === "Mayor a 3 días") return "Más de 3 días sin ciclos";
  if (Number.isFinite(Date.parse(row.sin_ciclos))) return parkingDate(row.sin_ciclos);
  return row.sin_ciclos === "Con ciclos" ? "Con actividad reportada" : "Sin datos";
}
export function parkingStats(rows) {
  const sum = (key) => rows.reduce((n, row) => n + (parkingNumber(row[key]) ?? 0), 0);
  return {
    totalComercios: rows.length,
    comerciosOperativos: rows.filter((r) => r.severity === "success").length,
    comerciosConFalla: rows.filter((r) => ["error", "warning"].includes(r.severity)).length,
    comerciosFallaTotal: rows.filter((r) => r.severity === "error").length,
    comerciosFallaParcial: rows.filter((r) => r.severity === "warning").length,
    comerciosSinTerminales: rows.filter((r) => !r.total_terminals).length,
    totalTerminals: sum("total_terminals"),
    terminalsSinNovedad: sum("terminals_sin_novedad"),
    terminalsPosibleFallo: sum("terminals_posible_fallo"),
    terminalsConNovedad: sum("terminals_con_novedad"),
    terminalsSinDatos: sum("terminals_sin_datos"),
    totalCiclosAbiertos: sum("ciclos_abiertos"),
    comerciosConCiclosAbiertos: rows.filter((r) => r.ciclos_abiertos > 0).length,
    comerciosSinDatosCiclos: rows.filter((r) => r.ciclos_abiertos == null).length,
    comerciosMas3DiasSinCiclos: rows.filter((r) => inactiveParking(r)).length,
  };
}
export function consolidateParking(terminals, reports) {
  const map = new Map();
  const ensure = (name) => {
    const id = parkingKey(name);
    if (!id) return null;
    if (!map.has(id))
      map.set(id, {
        id,
        comercio: clean(name),
        terminals: [],
        tipo_comercio: "No especificado",
        tipo_integracion: "No especificado",
        ciclos_abiertos: null,
        sin_ciclos: "Sin datos",
        sourceStates: [],
      });
    return map.get(id);
  };
  reports.forEach((report) => {
    const row = ensure(report.comercio);
    if (!row) return;
    row.ciclos_abiertos = parkingNumber(report.ciclos_abiertos);
    row.sin_ciclos = clean(report.sin_ciclos) || "Sin datos";
    row.tipo_comercio = clean(report.tipo_comercio) || "No especificado";
    row.tipo_integracion = clean(report.tipo_integracion) || "No especificado";
  });
  terminals.forEach((terminal) => {
    const row = ensure(terminal.comercio);
    if (!row) return;
    row.sourceStates.push(parkingKey(terminal.estado_comercio));
    row.terminals.push({
      ...terminal,
      estado_terminal: parkingKey(terminal.estado_terminal) || "SIN DATOS",
      promedio_minutos: parkingNumber(terminal.promedio_minutos),
      minutos_desde_ultima: parkingNumber(terminal.minutos_desde_ultima),
    });
  });
  return [...map.values()]
    .map((row) => {
      row.total_terminals = row.terminals.length;
      row.terminals_sin_novedad = row.terminals.filter(
        (t) => t.estado_terminal === "SIN NOVEDAD"
      ).length;
      row.terminals_posible_fallo = row.terminals.filter(
        (t) => t.estado_terminal === "POSIBLE FALLO"
      ).length;
      row.terminals_con_novedad = row.terminals.filter(
        (t) => t.estado_terminal === "CON NOVEDAD"
      ).length;
      row.terminals_sin_datos =
        row.total_terminals -
        row.terminals_sin_novedad -
        row.terminals_posible_fallo -
        row.terminals_con_novedad;
      const states = row.sourceStates;
      if (!row.total_terminals) {
        row.severity = "info";
        row.estado_general = "SIN TERMINALES";
      } else if (
        states.some((s) => ["COMERCIO CON FALLO", "FALLA TOTAL", "FALLO TOTAL"].includes(s))
      ) {
        row.severity = "error";
        row.estado_general = "COMERCIO CON FALLO";
      } else if (states.some((s) => ["FALLA PARCIAL DEL COMERCIO", "FALLA PARCIAL"].includes(s))) {
        row.severity = "warning";
        row.estado_general = "FALLA PARCIAL";
      } else if (states.includes("COMERCIO ACTIVO")) {
        row.severity = "success";
        row.estado_general = "OPERATIVO";
      } else if (row.terminals_con_novedad === row.total_terminals) {
        row.severity = "error";
        row.estado_general = "COMERCIO CON FALLO";
      } else if (row.terminals_con_novedad + row.terminals_posible_fallo) {
        row.severity = "warning";
        row.estado_general = "FALLA PARCIAL";
      } else if (row.terminals_sin_datos) {
        row.severity = "info";
        row.estado_general = "SIN DATOS";
      } else {
        row.severity = "success";
        row.estado_general = "OPERATIVO";
      }
      return row;
    })
    .sort(
      (a, b) =>
        ({ error: 0, warning: 1, info: 2, success: 3 }[a.severity] -
          { error: 0, warning: 1, info: 2, success: 3 }[b.severity] ||
        (b.ciclos_abiertos ?? -1) - (a.ciclos_abiertos ?? -1) ||
        a.comercio.localeCompare(b.comercio, "es"))
    );
}
export function filterParking(rows, filters) {
  const query = parkingKey(filters.busqueda);
  return rows.filter(
    (r) =>
      (!query ||
        [r.comercio, r.tipo_comercio, ...r.terminals.map((t) => t.terminalname)].some((v) =>
          parkingKey(v).includes(query)
        )) &&
      (filters.tipoComercio === "todos" || r.tipo_comercio === filters.tipoComercio) &&
      (filters.estado === "todos" ||
        r.severity ===
          {
            operativo: "success",
            falla_parcial: "warning",
            falla_total: "error",
            sin_datos: "info",
          }[filters.estado]) &&
      (filters.estadoCiclos === "todos" ||
        (filters.estadoCiclos === "con_ciclos_abiertos" && r.ciclos_abiertos > 0) ||
        (filters.estadoCiclos === "sin_ciclos_abiertos" && r.ciclos_abiertos === 0) ||
        (filters.estadoCiclos === "mas_3_dias" && inactiveParking(r)))
  );
}

export function parkingView(rows, view) {
  if (view === "fail") return rows.filter((row) => ["error", "warning"].includes(row.severity));
  if (view === "open")
    return rows
      .filter((row) => row.ciclos_abiertos > 0)
      .sort((a, b) => b.ciclos_abiertos - a.ciclos_abiertos);
  if (view === "inactive") return rows.filter((row) => inactiveParking(row));
  return rows;
}
